import { useState, useEffect } from 'react';
import SEO from '@/components/SEO';
import axios from 'axios';
import { API_URL } from '@/lib/utils';
import { Camera, Calendar, X } from 'lucide-react';

const TrainingGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await axios.get(`${API_URL}/training-photos?active_only=true`);
        setPhotos(res.data);
      } catch (error) {
        console.error('Error fetching photos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  return (
    <div data-testid="training-gallery-page">
      <SEO
        title="Training Gallery - Photos & Events"
        description="View photos from our healthcare training programs, workshops, and certification events."
        keywords="healthcare training photos, NABH training gallery, hospital staff training"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1a2a5e] to-[#3b7dd8] py-16 md:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Our Training Events</span>
          <h1 className="font-manrope text-4xl md:text-5xl font-bold mt-3 mb-6">
            Training Gallery
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Glimpses from our healthcare training programs, workshops, and certification events
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading gallery...</div>
          ) : photos.length === 0 ? (
            <div className="text-center py-16">
              <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Training photos coming soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="group cursor-pointer overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                  onClick={() => setSelectedPhoto(photo)}
                  data-testid={`gallery-photo-${photo.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={photo.image_url}
                      alt={photo.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-semibold text-lg">{photo.title}</h3>
                        {photo.event_name && (
                          <p className="text-white/80 text-sm">{photo.event_name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-manrope font-bold text-[#1a2a5e]">{photo.title}</h3>
                    {photo.event_name && (
                      <p className="text-sm text-gray-500 mt-1">{photo.event_name}</p>
                    )}
                    {photo.event_date && (
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                        <Calendar size={12} />
                        <span>{photo.event_date}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
          data-testid="lightbox-modal"
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
            data-testid="close-lightbox"
          >
            <X size={32} />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.image_url}
              alt={selectedPhoto.title}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4 text-white">
              <h3 className="text-xl font-bold">{selectedPhoto.title}</h3>
              {selectedPhoto.description && (
                <p className="text-gray-300 mt-1">{selectedPhoto.description}</p>
              )}
              {selectedPhoto.event_name && (
                <p className="text-gray-400 text-sm mt-2">{selectedPhoto.event_name} {selectedPhoto.event_date && `| ${selectedPhoto.event_date}`}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingGallery;
